import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import axios from "axios";
import {
  ArrowLeft,
  Share2,
  Copy,
  Check,
  Download,
  Twitter,
  Instagram,
  Flame,
  Trophy,
  Target
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const LOGO_URL = "https://customer-assets.emergentagent.com/job_3e64acab-2561-42e6-b860-06b1a563df4d/artifacts/5nvw3yv2_6ECA6BBA-F429-4238-A743-EE6BF2D1D33F.png";

export default function ShareProgress() {
  const { user } = useAuth();
  const [cardData, setCardData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    fetchCardData();
  }, []);

  const fetchCardData = async () => {
    try {
      const response = await axios.get(`${API}/share/progress-card`);
      setCardData(response.data);
    } catch (error) {
      console.error("Failed to fetch card data:", error);
    } finally {
      setLoading(false);
    }
  };

  const copyShareText = () => {
    navigator.clipboard.writeText(cardData?.share_text || "");
    setCopied(true);
    toast.success("Share text copied!");
    setTimeout(() => setCopied(false), 2000);
  };

  const shareToTwitter = () => {
    const text = encodeURIComponent(cardData?.share_text || "");
    window.open(`https://twitter.com/intent/tweet?text=${text}`, '_blank');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-primary animate-pulse text-2xl font-bold">LOADING...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-40 bg-background/80 backdrop-blur-lg border-b border-border">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to="/dashboard" className="flex items-center gap-2 text-muted-foreground hover:text-foreground">
              <ArrowLeft className="h-5 w-5" /> Back
            </Link>
            <div className="flex items-center gap-2">
              <img src={LOGO_URL} alt="Coach Coop" className="h-8 w-8 rounded-sm" />
              <span className="font-bold tracking-tight">SHARE PROGRESS</span>
            </div>
            <div className="w-16" />
          </div>
        </div>
      </header>

      <main className="pt-24 pb-12 px-4 sm:px-6 lg:px-8 max-w-2xl mx-auto">
        {/* Progress Card Preview */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8"
        >
          <Card className="bg-gradient-to-br from-primary/20 via-card to-accent/10 border-primary overflow-hidden">
            <CardContent className="p-0">
              {/* Card Header */}
              <div className="bg-primary/30 p-4 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <img src={LOGO_URL} alt="Coach Coop" className="h-10 w-10 rounded-sm" />
                  <div>
                    <div className="text-xs text-primary-foreground/70 uppercase tracking-wider">60-Day SHRED</div>
                    <div className="font-bold text-primary-foreground">COACH COOP</div>
                  </div>
                </div>
              </div>
              
              {/* Card Body */}
              <div className="p-6">
                <div className="text-center mb-6">
                  <div className="text-sm text-muted-foreground uppercase tracking-wider mb-1">{cardData?.name}</div>
                  <div className="text-6xl font-black text-primary font-mono mb-2">DAY {cardData?.current_day}</div>
                </div>
                
                <div className="grid grid-cols-3 gap-4 mb-6">
                  <div className="text-center p-3 bg-secondary/50 rounded-sm">
                    <Flame className="h-6 w-6 text-orange-500 mx-auto mb-1" />
                    <div className="text-2xl font-black font-mono">{cardData?.streak}</div>
                    <div className="text-xs text-muted-foreground uppercase">Streak</div>
                  </div>
                  <div className="text-center p-3 bg-secondary/50 rounded-sm">
                    <Trophy className="h-6 w-6 text-accent mx-auto mb-1" />
                    <div className="text-2xl font-black font-mono">{cardData?.total_workouts}</div>
                    <div className="text-xs text-muted-foreground uppercase">Workouts</div>
                  </div>
                  <div className="text-center p-3 bg-secondary/50 rounded-sm">
                    <Target className="h-6 w-6 text-primary mx-auto mb-1" />
                    <div className="text-2xl font-black font-mono">{cardData?.discipline_score}</div>
                    <div className="text-xs text-muted-foreground uppercase">Score</div>
                  </div>
                </div>
                
                {/* Badge */}
                <div className="text-center p-4 bg-secondary/30 rounded-sm border border-border">
                  <div 
                    className="inline-block px-4 py-2 rounded-sm font-bold uppercase tracking-wider"
                    style={{ 
                      backgroundColor: `${cardData?.badge?.color}20`,
                      color: cardData?.badge?.color,
                      border: `1px solid ${cardData?.badge?.color}`
                    }}
                  >
                    {cardData?.badge?.name}
                  </div>
                </div>
              </div>
              
              {/* Card Footer */}
              <div className="bg-secondary/50 p-4 text-center">
                <div className="text-sm text-muted-foreground">coachcooptraining.com</div>
              </div>
            </CardContent>
          </Card>
        </motion.div>

        {/* Share Text */}
        <Card className="bg-card border-border mb-6">
          <CardContent className="p-4">
            <div className="text-sm text-muted-foreground uppercase tracking-wider mb-2">Share Text</div>
            <p className="text-foreground mb-4">{cardData?.share_text}</p>
            <Button
              data-testid="copy-share-text-btn"
              onClick={copyShareText}
              variant="outline"
              className="w-full uppercase font-bold tracking-widest rounded-sm"
            >
              {copied ? <Check className="mr-2 h-4 w-4 text-accent" /> : <Copy className="mr-2 h-4 w-4" />}
              {copied ? "Copied!" : "Copy Text"}
            </Button>
          </CardContent>
        </Card>

        {/* Share Buttons */}
        <div className="grid grid-cols-2 gap-4">
          <Button
            data-testid="share-twitter-btn"
            onClick={shareToTwitter}
            className="bg-[#1DA1F2] hover:bg-[#1DA1F2]/90 uppercase font-bold tracking-widest rounded-sm"
          >
            <Twitter className="mr-2 h-4 w-4" /> Share on X
          </Button>
          <Button
            variant="outline"
            onClick={() => toast.info("Instagram sharing coming soon!")}
            className="uppercase font-bold tracking-widest rounded-sm"
          >
            <Instagram className="mr-2 h-4 w-4" /> Instagram
          </Button>
        </div>

        <p className="text-center text-sm text-muted-foreground mt-6">
          Share your progress to inspire others and hold yourself accountable!
        </p>
      </main>
    </div>
  );
}
